'use client';

import Link from 'next/link';
import { Building2, Stethoscope, ArrowRight } from 'lucide-react';
import { PublicSection } from '../../../../components/public/PublicSection';
import { PROJECT_NAME } from '@shared/constants';

/**
 * @group Landing
 * @summary Audience teasers section linking to Employers and Doctors pages
 */
export function AudienceTeasersSection() {
  return (
    <PublicSection
      indented
      className="relative overflow-hidden bg-brand-mint"
      style={{
        boxShadow: 'inset 0 12px 24px rgba(0, 0, 0, 0.4), inset 0 6px 12px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(0, 0, 0, 0.2)',
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Employers Card */}
        <Link
          href="/employers"
          className="group relative overflow-hidden rounded-2xl border border-brand-teal/20 bg-white/80 p-8 shadow-[0_8px_30px_rgba(8,67,73,0.08)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(8,67,73,0.15)] focus-visible:ring-2 focus-visible:ring-brand-teal"
        >
          <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-brand-teal/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal ring-1 ring-brand-teal/15">
              <Building2 className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="mb-2 text-2xl font-semibold text-[#084349] transition-colors">
                For Employers: Better Health, Better Business
              </h3>
              <p className="mb-4 leading-relaxed text-[#084349]/85">
                Forward-thinking South African employers are discovering that providing {PROJECT_NAME} as an employee benefit delivers measurable returns. Healthier employees mean reduced absenteeism, improved productivity, and a workforce that feels genuinely valued and supported.
              </p>
              <div className="flex items-center gap-2 font-semibold text-brand-teal">
                Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </Link>

        {/* Doctors Card */}
        <Link
          href="/doctors"
          className="group relative overflow-hidden rounded-2xl border border-brand-teal/20 bg-white/80 p-8 shadow-[0_8px_30px_rgba(8,67,73,0.08)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(8,67,73,0.15)] focus-visible:ring-2 focus-visible:ring-brand-teal"
        >
          <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-brand-teal/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal ring-1 ring-brand-teal/15">
              <Stethoscope className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="mb-2 text-2xl font-semibold text-[#084349] transition-colors">
                For Clinicians: Apply for a Fulltime Role
              </h3>
              <p className="mb-4 leading-relaxed text-[#084349]/85">
                Join {PROJECT_NAME} as a fulltime clinician and help deliver accessible, high-quality
                healthcare at scale. We provide modern systems, strong clinical governance, and a team
                culture built around patient outcomes and professional growth.
              </p>
              <div className="flex items-center gap-2 font-semibold text-brand-teal">
                Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </Link>
      </div>
    </PublicSection>
  );
}
